// Ported from greenline-app (TabletPOS) PR #2837. Keep in sync until it moves to @getgreenline/homi-shared.
import { THEME_COLORS } from '@getgreenline/shared'
import {
  IUberEatsAlertTiming,
  IUberEatsDenyReasonOption,
  IUberEatsTimerConfig,
  UberEatsNewOrderPauseOption,
  UberEatsUrgencyStage,
} from '../models/UberEats/UberEatsModels'

const { BRAND, ACTIONS, MONO } = THEME_COLORS
// Theme accents on their own 15% tint are 2:1 to 3:1, so text stays dark and the stage is also named in words
const TINT_ALPHA = '26'
const tint = (hex: string) => `${hex}${TINT_ALPHA}`

export const UBER_EATS_SLA_MS = 11.5 * 60 * 1000

export const UBER_EATS_TIMER_CONFIG: IUberEatsTimerConfig = {
  slaMs: UBER_EATS_SLA_MS,
  urgentChimeRepeatMs: 15 * 1000,
  tickMs: 1000,
}

// TODO: move alert timing to server-side device/location settings
export const UBER_EATS_DEFAULT_ALERT_TIMING: IUberEatsAlertTiming = {
  urgentMinutes: 3,
  warningMinutes: 6,
}
export const UBER_EATS_ALERT_TIMING_LIMITS = {
  urgentMin: 1,
  urgentMax: 9,
  warningMax: 10,
}

// Time-left pill: the only card element whose style follows urgency
export const UBER_EATS_TIME_PILL_STYLES: Record<
  UberEatsUrgencyStage | 'expired',
  { ink: string; bg: string; border?: string }
> = {
  normal: { ink: BRAND.black, bg: tint(BRAND.growAlt) },
  warning: { ink: BRAND.black, bg: tint(BRAND.distroAlt) },
  urgent: { ink: BRAND.black, bg: tint(ACTIONS.dangerAlt), border: ACTIONS.dangerAlt },
  expired: { ink: MONO.mono33, bg: MONO.monoee, border: MONO.monocc }, // TODO: GR-14015
}

// Constant card chips (do not follow urgency)
export const UBER_EATS_CHIP_STYLES = {
  displayId: { ink: MONO.mono33, bg: MONO.monoee },
  brand: { ink: MONO.monoFf, bg: BRAND.black },
  awaiting: { ink: BRAND.black, bg: tint(BRAND.distroAlt) },
}

export const UBER_EATS_STAGE_LABELS: Record<UberEatsUrgencyStage | 'expired', string> = {
  normal: 'New',
  warning: 'Respond soon',
  urgent: 'Urgent',
  expired: 'Expired',
}

export const UBER_EATS_ITEM_QTY_COLORS = {
  default: MONO.mono77,
  short: ACTIONS.dangerAlt,
}

// Green, distinct from the red online-orders badge. No theme green is dark enough for white text,
// so the count is dark on green (about 5.8:1).
export const UBER_EATS_BADGE_COLOR = BRAND.growAlt
export const UBER_EATS_BADGE_INK = BRAND.black
export const UBER_EATS_ACCEPT_COLOR = BRAND.growAlt

// TODO: GR-14036 — reconcile labels with UX reasons and extract to shared logic
export const UBER_EATS_DENY_REASONS: IUberEatsDenyReasonOption[] = [
  {
    code: 'ITEM_AVAILABILITY',
    label: 'Item unavailable',
    requiresExplanation: false,
  },
  { code: 'MISSING_ITEM', label: 'Missing item', requiresExplanation: false },
  { code: 'CAPACITY', label: 'Too busy', requiresExplanation: false },
  { code: 'STORE_CLOSED', label: 'Store closed', requiresExplanation: false },
  { code: 'POS_NOT_READY', label: 'POS not ready', requiresExplanation: false },
  { code: 'POS_OFFLINE', label: 'POS offline', requiresExplanation: false },
  { code: 'PRICING', label: 'Pricing issue', requiresExplanation: false },
  {
    code: 'MISSING_INFO',
    label: 'Missing information',
    requiresExplanation: false,
  },
  { code: 'ADDRESS', label: 'Address issue', requiresExplanation: false },
  {
    code: 'SPECIAL_INSTRUCTIONS',
    label: "Can't follow special instructions",
    requiresExplanation: false,
  },
  { code: 'OTHER', label: 'Other', requiresExplanation: true },
]

export const UBER_EATS_NEW_ORDER_PAUSE_OPTIONS: {
  option: UberEatsNewOrderPauseOption
  label: string
}[] = [
  { option: 'PAUSE_20_MIN', label: 'Snooze for 20 min' },
  { option: 'PAUSE_40_MIN', label: 'Snooze for 40 min' },
  { option: 'UNTIL_TOMORROW', label: 'Snooze until tomorrow' },
]

export const UBER_EATS_PLACEHOLDERS = {
  displayId: '----',
  customerName: 'Uber Eats customer',
  itemName: 'Unnamed item',
}
